import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Button from '../../../components/elements/button';

interface Props {
    _ref: React.RefObject<HTMLDialogElement>;
    originalStartDate: string;
    initialHours: number;
    initialMinutes: number;
    initialSeconds: number;
    onResolved: (completionDateTime: string) => void;
}

const TaskExecutionTimeModal: React.FC<Props> = ({
    _ref,
    originalStartDate,
    initialHours,
    initialMinutes,
    initialSeconds,
    onResolved,
}) => {
    const [hours, setHours] = useState<number>(initialHours);
    const [minutes, setMinutes] = useState<number>(initialMinutes);
    const [seconds, setSeconds] = useState<number>(initialSeconds);

    useEffect(() => {
        setHours(initialHours);
        setMinutes(initialMinutes);
        setSeconds(initialSeconds);
    }, [initialHours, initialMinutes, initialSeconds]);

    const calcCompletionDateTime = (h: number, m: number, s: number): string => {
        const totalInputSeconds = (h * 3600) + (m * 60) + s;
        return moment.utc(originalStartDate).local()
            .add(totalInputSeconds, 'seconds')
            .format('YYYY-MM-DDTHH:mm:ss');
    };

    const handleContinue = () => {
        const completionDateTime = calcCompletionDateTime(initialHours, initialMinutes, initialSeconds);
        _ref?.current?.close();
        onResolved(completionDateTime);
    };

    const handleUpdate = () => {
        const completionDateTime = calcCompletionDateTime(hours, minutes, seconds);
        _ref?.current?.close();
        onResolved(completionDateTime);
    };

    return (
        <dialog ref={_ref} className="modal">
            <div className="modal-box max-w-[90%] w-[28rem] p-0">
                <div className='flex items-center justify-between border-b border-[#8D6E63] p-4 sticky top-0 bg-white z-10'>
                    <p className='text-[1.063rem] text-[#755B51] font-semibold'>Task Execution Time</p>
                </div>

                <div className='px-6 py-5'>
                    <p className='text-sm text-gray-500 mb-5'>
                        This task has been running for over 3 hours. Please confirm or adjust the execution time before completing.
                    </p>
                    <div className='flex items-end gap-4'>
                        <div className='flex flex-col gap-1 flex-1'>
                            <label className='text-[0.813rem] font-medium text-[#262A2D]'>Hours</label>
                            <input
                                type="number"
                                min={0}
                                className='border border-gray-300 rounded-md px-3 py-2 text-center text-[1rem] font-semibold outline-none focus:border-primary w-full'
                                value={hours}
                                onChange={(e) => setHours(Math.max(0, +e.target.value || 0))}
                            />
                        </div>
                        <span className='text-[1.5rem] font-bold text-[#2B5A2E] pb-2'>:</span>
                        <div className='flex flex-col gap-1 flex-1'>
                            <label className='text-[0.813rem] font-medium text-[#262A2D]'>Minutes</label>
                            <input
                                type="number"
                                min={0}
                                max={59}
                                className='border border-gray-300 rounded-md px-3 py-2 text-center text-[1rem] font-semibold outline-none focus:border-primary w-full'
                                value={minutes}
                                onChange={(e) => setMinutes(Math.max(0, Math.min(59, +e.target.value || 0)))}
                            />
                        </div>
                        <span className='text-[1.5rem] font-bold text-[#2B5A2E] pb-2'>:</span>
                        <div className='flex flex-col gap-1 flex-1'>
                            <label className='text-[0.813rem] font-medium text-[#262A2D]'>Seconds</label>
                            <input
                                type="number"
                                min={0}
                                max={59}
                                className='border border-gray-300 rounded-md px-3 py-2 text-center text-[1rem] font-semibold outline-none focus:border-primary w-full'
                                value={seconds}
                                onChange={(e) => setSeconds(Math.max(0, Math.min(59, +e.target.value || 0)))}
                            />
                        </div>
                    </div>
                </div>

                <div className='flex justify-end gap-2 border-t border-[#8D6E63] pt-3 pb-4 px-4 sticky bottom-0 bg-white z-10'>
                    <Button
                        type="button"
                        theme='secondary'
                        onClick={handleContinue}
                        customstyle={{ width: 110, height: 40 }}
                        childstyle={{ justifyContent: 'center', fontSize: 15 }}
                    >
                        Continue
                    </Button>
                    <Button
                        type="button"
                        onClick={handleUpdate}
                        customstyle={{ background: '#2D6A32', width: 110, height: 40 }}
                        childstyle={{ justifyContent: 'center', fontSize: 15 }}
                    >
                        Update
                    </Button>
                </div>
            </div>
        </dialog>
    );
};

export default TaskExecutionTimeModal;
